import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiHome, FiTrash2, FiRefreshCw, FiAlertCircle, FiSearch, FiX } from 'react-icons/fi';
import { getAllGuilds, deleteGuild } from './guildService';
import '../doctors/DoctorList.css'; // Reusing table styles

export default function GuildList() {
    const [guilds, setGuilds] = useState([]);
    const [filteredGuilds, setFilteredGuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        loadGuilds();
    }, []);

    useEffect(() => {
        if (search) {
            const lowSearch = search.toLowerCase();
            setFilteredGuilds(guilds.filter(g =>
                g.name?.toLowerCase().includes(lowSearch) ||
                g.area?.toLowerCase().includes(lowSearch)
            ));
        } else {
            setFilteredGuilds(guilds);
        }
    }, [guilds, search]);

    async function loadGuilds() {
        try {
            setLoading(true);
            const data = await getAllGuilds();
            setGuilds(data);
            setError('');
        } catch (err) {
            setError('Failed to load guilds');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(id, name) {
        if (!window.confirm(`Are you sure you want to delete "${name}"? This may affect doctors assigned to it.`)) {
            return;
        }

        try {
            await deleteGuild(id);
            await loadGuilds();
        } catch (err) {
            alert('Failed to delete guild');
            console.error(err);
        }
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading guilds...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Guilds</h1>
                </div>
                <Link to="/dashboard/guilds/create" className="btn-primary">
                    <FiHome />
                    <span>Add Guild</span>
                </Link>
            </div>

            {error && (
                <div className="error-message">
                    <FiAlertCircle />
                    <span>{error}</span>
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by guild name or area..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {search && (
                    <button onClick={() => setSearch('')} className="btn-clear-filters">
                        <FiX />
                        <span>Clear Filter</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Area</th>
                            <th>Description</th>
                            <th>Created At</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredGuilds.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-state">
                                    <div className="empty-content">
                                        <FiHome className="empty-icon" />
                                        <p>{search ? 'No guilds match your search.' : 'No guilds found. Create your first guild to start adding doctors.'}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredGuilds.map((guild) => (
                                <tr key={guild.id}>
                                    <td className="name-cell">
                                        <strong>{guild.name}</strong>
                                    </td>
                                    <td>{guild.area || 'N/A'}</td>
                                    <td>{guild.description || 'No description'}</td>
                                    <td>{guild.createdAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                                    <td className="actions-cell">
                                        <button
                                            onClick={() => handleDelete(guild.id, guild.name)}
                                            className="btn-delete"
                                            title="Delete guild"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
